import pdfWorkerAssetUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  PDF_PREVIEW_LONG_SIDE,
  alphaBounds,
  hasTransparency,
  imageNameForPdf,
  pdfRenderScale,
  shrinkFactorForSize,
  trimRenderScale,
  type PixelBounds,
} from './pdfHelpers';
import { t } from './i18n';

export interface RasterizedPdf {
  file: File;
  pageCount: number;
  /** 1-based page that was imported. */
  pageNumber: number;
  /** The page's empty margins were cut away. */
  trimmed: boolean;
  /** Effective resolution of the result relative to the PDF's physical size. */
  renderDpi: number;
}

type PdfJsModule = typeof import('pdfjs-dist');
type PdfLoadingTask = ReturnType<PdfJsModule['getDocument']>;
type PdfDocumentProxy = Awaited<PdfLoadingTask['promise']>;
export type PdfPageProxy = Awaited<ReturnType<PdfDocumentProxy['getPage']>>;
export type { PdfDocumentProxy };
type PdfWorkerInstance = InstanceType<PdfJsModule['PDFWorker']>;

class PdfWorkerStartupError extends Error {
  cause: unknown;

  constructor(cause: unknown) {
    super('PDF worker could not start');
    this.name = 'PdfWorkerStartupError';
    this.cause = cause;
  }
}

// ── Shared worker ──────────────────────────────────────────────
// The worker script is 1.2 MB to parse and boot, so one worker serves every upload of the page
// and is only torn down after a quiet spell. A PDFWorker handed to getDocument is not destroyed
// with the document (PDF.js only destroys workers it created itself).
const WORKER_IDLE_MS = 120_000;

interface SharedWorker {
  worker: Worker;
  pdfWorker: PdfWorkerInstance;
  failed: boolean;
}

let shared: SharedWorker | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;

function releaseWorker(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
  if (!shared) return;
  shared.pdfWorker.destroy();
  shared.worker.terminate();
  shared = null;
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', releaseWorker);

async function startWorker(pdfjs: PdfJsModule, url: URL): Promise<SharedWorker> {
  let worker: Worker;
  try {
    worker = new Worker(url, { type: 'module' });
  } catch (error) {
    throw new PdfWorkerStartupError(error);
  }
  const entry = { worker, pdfWorker: null as unknown as PdfWorkerInstance, failed: false };
  const failed = new Promise<never>((_, reject) => {
    worker.addEventListener('error', (event) => {
      entry.failed = true;
      reject(new PdfWorkerStartupError(event));
    });
  });
  try {
    // The typings declare `port` as null, but PDF.js accepts any Worker there (it is what
    // GlobalWorkerOptions.workerPort feeds it).
    type PdfWorkerParams = ConstructorParameters<PdfJsModule['PDFWorker']>[0];
    entry.pdfWorker = new pdfjs.PDFWorker({ port: worker } as unknown as PdfWorkerParams);
    await Promise.race([entry.pdfWorker.promise, failed]);
    return entry;
  } catch (error) {
    entry.pdfWorker?.destroy();
    worker.terminate();
    throw error instanceof PdfWorkerStartupError ? error : new PdfWorkerStartupError(error);
  }
}

async function acquireWorker(pdfjs: PdfJsModule): Promise<SharedWorker> {
  if (shared && !shared.failed && !shared.pdfWorker.destroyed) return shared;
  releaseWorker();
  // Keep the package-relative form as the preferred path. Some Vite/WordPress
  // combinations cannot rewrite it and return 404; retry with the emitted ?url
  // asset resolved from this addon's public path in that case.
  try {
    shared = await startWorker(pdfjs, new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url));
  } catch (error) {
    if (!(error instanceof PdfWorkerStartupError)) throw error;
    shared = await startWorker(pdfjs, fallbackWorkerUrl());
  }
  return shared;
}

/** One PDF at a time: jobs share the worker and the page picker is modal anyway. */
let queue: Promise<unknown> = Promise.resolve();
function runExclusive<T>(job: () => Promise<T>): Promise<T> {
  const next = queue.then(job, job);
  queue = next.catch(() => undefined);
  return next;
}

function pluginBaseUrl(): string {
  const marker = '/plugins/pdf-upload/';
  const script = Array.from(document.scripts).find((node) => node.src.includes(marker));
  const source = script?.src || import.meta.url;
  const markerIndex = source.indexOf(marker);
  if (markerIndex >= 0) return source.slice(0, markerIndex + marker.length);
  const current = new URL(import.meta.url);
  current.pathname = current.pathname.replace(/\/dist\/.*$/, '/');
  current.search = '';
  current.hash = '';
  return current.href;
}

function fallbackWorkerUrl(): URL {
  const emitted = String(pdfWorkerAssetUrl || '').trim();
  if (/^(?:https?:)?\/\//i.test(emitted)) return new URL(emitted, window.location.href);
  if (emitted.startsWith('/')) {
    return new URL(`dist/${emitted.replace(/^\/+/, '')}`, pluginBaseUrl());
  }
  return new URL(emitted, import.meta.url);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/png' | 'image/jpeg', quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not encode the PDF page'));
    }, type, quality);
  });
}

/** Drop a canvas' backing store right away (a 4096² page holds 64 MB; iOS caps total canvas memory). */
function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

function newCanvas(width: number, height: number): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width));
  canvas.height = Math.max(1, Math.ceil(height));
  const context = canvas.getContext('2d', { willReadFrequently: false });
  if (!context) throw new Error('Canvas is unavailable');
  return { canvas, context };
}

/** Copy `source` scaled by `factor`, on white when the target format has no alpha. */
function resample(source: HTMLCanvasElement, factor: number, opaque: boolean): HTMLCanvasElement {
  const { canvas, context } = newCanvas(source.width * factor, source.height * factor);
  if (opaque) {
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

interface EncodedPage {
  blob: Blob;
  extension: 'png' | 'jpg';
  width: number;
}

/**
 * Encode the render so it fits the store's upload cap: PNG first (keeps transparency); an
 * opaque page that is too big becomes JPEG; otherwise the canvas shrinks until it fits.
 */
async function encodeUnderLimit(canvas: HTMLCanvasElement, opaque: boolean, maxBytes: number): Promise<EncodedPage> {
  let blob = await canvasToBlob(canvas, 'image/png');
  if (!(maxBytes > 0) || blob.size <= maxBytes) return { blob, extension: 'png', width: canvas.width };

  const type = opaque ? 'image/jpeg' : 'image/png';
  const extension = opaque ? 'jpg' : 'png';
  let current = opaque ? resample(canvas, 1, true) : canvas;
  if (opaque) blob = await canvasToBlob(current, type, 0.92);
  for (let attempt = 0; attempt < 6 && blob.size > maxBytes; attempt++) {
    const next = resample(current, shrinkFactorForSize(blob.size, maxBytes), opaque);
    if (current !== canvas) releaseCanvas(current);
    current = next;
    blob = await canvasToBlob(current, type, opaque ? 0.92 : undefined);
  }
  const width = current.width;
  if (current !== canvas) releaseCanvas(current);
  return { blob, extension, width };
}

export interface RasterizeOptions {
  /** Store upload cap; the encoded page is fitted under it. 0 = no limit. */
  maxUploadBytes?: number;
  /** Asked for multi-page documents: resolves the 1-based page to import, or null to cancel. */
  pickPage?: (document: PdfDocumentProxy) => Promise<number | null>;
}

/** Shopper-facing failure, shown by the host instead of the generic "Could not read PDF". */
export class PdfUserError extends Error {
  userMessage: string;
  /** The shopper backed out; the host ends the upload without an error. */
  cancelled: boolean;

  constructor(userMessage: string, cause?: unknown, cancelled = false) {
    super(userMessage);
    this.name = 'PdfUserError';
    this.userMessage = userMessage;
    this.cancelled = cancelled;
    (this as { cause?: unknown }).cause = cause;
  }
}

/** Map PDF.js failures to messages a shopper can act on; anything else stays generic. */
function toUserError(error: unknown): unknown {
  if (error instanceof PdfUserError) return error;
  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'PasswordException') return new PdfUserError(t('This PDF is password-protected. Remove the password and try again.'), error);
  if (name === 'InvalidPDFException') return new PdfUserError(t('This file is not a valid PDF.'), error);
  return error;
}

interface RenderedPage {
  file: File;
  trimmed: boolean;
  renderDpi: number;
}

/** Render one page: find the artwork, render just that at print resolution, fit the upload cap. */
async function renderPage(page: PdfPageProxy, sourceFile: File, options: RasterizeOptions): Promise<RenderedPage> {
  const baseViewport = page.getViewport({ scale: 1 });

  // Both passes render transparent (no white page) with the print intent, which also keeps
  // PDF.js off requestAnimationFrame so a background tab doesn't stall the upload.
  const render = (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, scale: number, offsetX = 0, offsetY = 0) =>
    page.render({
      canvas,
      canvasContext: context,
      viewport: page.getViewport({ scale }),
      intent: 'print',
      background: 'rgba(0,0,0,0)',
      transform: offsetX || offsetY ? [1, 0, 0, 1, -offsetX, -offsetY] : undefined,
    }).promise;

  // Pass 1: low-resolution render to find where the artwork is.
  const previewScale = PDF_PREVIEW_LONG_SIDE / Math.max(baseViewport.width, baseViewport.height);
  const preview = newCanvas(baseViewport.width * previewScale, baseViewport.height * previewScale);
  await render(preview.canvas, preview.context, previewScale);
  const previewData = preview.context.getImageData(0, 0, preview.canvas.width, preview.canvas.height);
  releaseCanvas(preview.canvas);
  const bounds = alphaBounds(previewData.data, previewData.width, previewData.height);
  if (!bounds) throw new PdfUserError(t('This PDF page is empty.'));
  const pageAreaPx = previewData.width * previewData.height;
  const trimmed = bounds.width * bounds.height < pageAreaPx * 0.98;
  // Judge opacity inside the 1 px margin: the margin and a partially covered last preview
  // row/column are always see-through and would otherwise make every page "transparent".
  const opaque = !hasTransparency(cropRgba(previewData, insetBounds(bounds, 1)));

  // Pass 2: render just the artwork at full resolution.
  const boxW = bounds.width / previewScale;
  const boxH = bounds.height / previewScale;
  const scale = trimmed ? trimRenderScale(boxW, boxH) : pdfRenderScale(baseViewport.width, baseViewport.height);
  const full = newCanvas(boxW * scale, boxH * scale);
  await render(full.canvas, full.context, scale, (bounds.x / previewScale) * scale, (bounds.y / previewScale) * scale);

  const encoded = await encodeUnderLimit(full.canvas, opaque, options.maxUploadBytes ?? 0);
  releaseCanvas(full.canvas);
  const effectiveScale = scale * (encoded.width / Math.max(1, Math.ceil(boxW * scale)));
  return {
    file: new File([encoded.blob], imageNameForPdf(sourceFile.name, encoded.extension), {
      type: encoded.extension === 'jpg' ? 'image/jpeg' : 'image/png',
      lastModified: Date.now(),
    }),
    trimmed,
    renderDpi: Math.round(effectiveScale * 72),
  };
}

function insetBounds(bounds: PixelBounds, inset: number): PixelBounds {
  if (bounds.width <= inset * 2 || bounds.height <= inset * 2) return bounds;
  return { x: bounds.x + inset, y: bounds.y + inset, width: bounds.width - inset * 2, height: bounds.height - inset * 2 };
}

/** RGBA pixels of `bounds` inside `image`. */
function cropRgba(image: ImageData, bounds: PixelBounds): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bounds.width * bounds.height * 4);
  for (let row = 0; row < bounds.height; row++) {
    const from = ((bounds.y + row) * image.width + bounds.x) * 4;
    out.set(image.data.subarray(from, from + bounds.width * 4), row * bounds.width * 4);
  }
  return out;
}

export async function rasterizePdfToPng(file: File, options: RasterizeOptions = {}): Promise<RasterizedPdf> {
  return runExclusive(async () => {
    if (idleTimer) clearTimeout(idleTimer);
    let documentProxy: PdfDocumentProxy | null = null;
    let loadingTask: PdfLoadingTask | null = null;
    let page: PdfPageProxy | null = null;
    try {
      const pdfjs = await import('pdfjs-dist');
      const bytes = new Uint8Array(await file.arrayBuffer());
      const { pdfWorker } = await acquireWorker(pdfjs);
      loadingTask = pdfjs.getDocument({ data: bytes, worker: pdfWorker });
      documentProxy = await loadingTask.promise;

      const pageCount = documentProxy.numPages;
      let pageNumber = 1;
      if (pageCount > 1 && options.pickPage) {
        const picked = await options.pickPage(documentProxy);
        if (picked == null) throw new PdfUserError(t('Cancelled'), undefined, true);
        pageNumber = Math.min(pageCount, Math.max(1, Math.round(picked)));
      }

      page = await documentProxy.getPage(pageNumber);
      const rendered = await renderPage(page, file, options);
      return { ...rendered, pageCount, pageNumber };
    } catch (error) {
      // A worker that died mid-job is not reused.
      if (shared && (shared.failed || shared.pdfWorker.destroyed)) releaseWorker();
      throw toUserError(error);
    } finally {
      page?.cleanup();
      if (documentProxy) await documentProxy.destroy().catch(() => undefined);
      else if (loadingTask) await loadingTask.destroy().catch(() => undefined);
      if (shared) {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(releaseWorker, WORKER_IDLE_MS);
      }
    }
  });
}
